import React, { useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { semantic_colors, shapes, text_styles } from '../../foundation';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { CloseButton } from '../button';
import { Button } from '../button';
import {
  IconArrowChevronRight,
  IconCircleInfoFill,
  IconCircleWarningFill,
  IconCircleCheckFill,
} from '../icons/generated';
import { notification_hide_animation, notification_status_css } from './styles';
import type { NotificationPositionType, NotificationStatus } from './types';
import { useProviderConfig } from '../provider';

export interface NotificationComponentProps {
  /**
   * id값을 지정할 수 있습니다.
   *  - id값은 unique 해야합니다.
   *  - 해당 id값으로 destroy 수동 처리가 가능합니다.
   *  - 지정하지 않을 경우 messageManager generateMessageId를 통해 자동으로 생성되어 관리됩니다.
   */
  id?: string;
  /** @default 'info' */
  status?: NotificationStatus;
  title: string;
  content: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /**
   * 하단 좌측 링크 텍스트
   */
  linkText?: string;
  /**
   * 닫힘 버튼 사용 유무
   * @default false
   */
  closable?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  onClickLink?: () => void;
  destroy: () => void;
  position: NotificationPositionType;
}

export const NotificationComponent: React.FC<NotificationComponentProps> = React.memo(
  ({
    status = 'info',
    title,
    content,
    confirmText,
    cancelText,
    linkText,
    closable = false,
    onConfirm,
    onCancel,
    onClose,
    onClickLink,
    destroy,
    position,
  }) => {
    const { locale } = useProviderConfig();
    const [show, setShow] = useState(true);
    const hasAction = confirmText || cancelText;

    const close = () => {
      setShow(false);
      destroy();
      onClose?.();
    };

    const handleConfirm = () => {
      onConfirm?.();
      close();
    };

    const handleCancel = () => {
      onCancel?.();
      close();
    };

    const renderToIcon = () => {
      const color = notification_status_css[status];
      const size = 20;
      return (
        <>
          {status === 'info' && (
            <IconCircleInfoFill size={size} color={color} aria-label={locale?.accessibility.info} />
          )}
          {status === 'success' && (
            <IconCircleCheckFill size={size} color={color} aria-label={locale?.accessibility.success} />
          )}
          {(status === 'error' || status === 'warning') && (
            <IconCircleWarningFill
              size={size}
              color={color}
              aria-label={status === 'error' ? locale?.accessibility.error : locale?.accessibility.warning}
            />
          )}
        </>
      );
    };

    return (
      <>
        <Container direction='column' visible={show} position={position} gap={32} p={24} role='status' as='section'>
          <Stack direction='column' gap={8}>
            <Stack direction='row' gap={8} align='center'>
              {renderToIcon()}
              <BaseText typography='Heading_17_Bold' color={notification_status_css[status]} as='h1'>
                {title}
              </BaseText>
              {closable && (
                <Stack direction='row' ml='auto'>
                  <CloseButton onClick={close} />
                </Stack>
              )}
            </Stack>
            {content}
          </Stack>
          <Stack direction='row' justify={hasAction ? 'space-between' : undefined} align='center'>
            {linkText && (
              <LinkText direction='row' gap={2} align='center' onClick={onClickLink} role='button' tabIndex={0}>
                <BaseText typography='Body_12_SemiBold' color={semantic_colors.content.secondary}>
                  {linkText}
                </BaseText>
                <IconArrowChevronRight color={semantic_colors.content.secondary} aria-hidden size={12} />
              </LinkText>
            )}
            {hasAction && (
              <Stack direction='row' gap={4} ml='auto'>
                {cancelText && <Button onClick={handleCancel}>{cancelText}</Button>}
                {confirmText && (
                  <Button variant='filled' color='primary' onClick={handleConfirm}>
                    {confirmText}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Container>
      </>
    );
  },
  (prevProps: NotificationComponentProps, nextProps: NotificationComponentProps) => {
    return prevProps.id === nextProps.id;
  },
);

const Container = styled(Stack)<{ visible: boolean; position: NotificationPositionType }>`
  width: 100%;
  max-width: 380px;
  background-color: ${semantic_colors.background.elevated};
  color: ${semantic_colors.content.primary};
  ${text_styles.Body_13_Regular};
  ${shapes.border_radius.small};
  ${shapes.shadow.medium};
  word-break: break-all;
  ${({ visible, position }) =>
    visible &&
    css`
      animation: ${notification_hide_animation(position)} ease-in-out 0.2s forwards;
    `}
`;

// TODO(@olive-sally): linkText => TextButton 추가후 대체
const LinkText = styled(Stack)`
  cursor: pointer;
`;
